<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { aiApi, type AiSearchResult } from '@/api/ai'
import { useAiChatStore } from '@/stores/ai-chat'
import { resourceRefToPath } from '@/lib/resource-links'
import {
  Dialog,
  DialogContent,
} from '@kyoto2/components/ui/dialog'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@kyoto2/components/ui/command'
import { Brain, Search } from 'lucide-vue-next'
import { toast } from 'vue-sonner'

const open = ref(false)
const query = ref('')
const results = ref<AiSearchResult[]>([])
const loading = ref(false)
const router = useRouter()
const aiChat = useAiChatStore()

let debounce: ReturnType<typeof setTimeout> | undefined
let searchGeneration = 0

watch(query, (q) => {
  clearTimeout(debounce)
  if (!q.trim()) {
    results.value = []
    return
  }
  debounce = setTimeout(async () => {
    const gen = ++searchGeneration
    loading.value = true
    try {
      const res = await aiApi.search(q.trim(), undefined)
      if (gen !== searchGeneration) return
      results.value = res.results
    } catch (err) {
      if (gen !== searchGeneration) return
      results.value = []
      toast.error('Search failed', { description: (err as Error).message })
    } finally {
      if (gen === searchGeneration) loading.value = false
    }
  }, 200)
})

function onKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    open.value = true
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

function navigate(result: AiSearchResult) {
  open.value = false
  query.value = ''
  router.push(resourceRefToPath(result.resource_ref))
}

function askAi(result: AiSearchResult) {
  open.value = false
  query.value = ''
  aiChat.openWithContext({
    message: `Tell me about ${result.name}`,
    routeContext: `${result.type} ${result.resource_ref.id}`,
  })
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="p-0 gap-0 max-w-lg overflow-hidden">
      <Command>
        <CommandInput v-model="query" placeholder="Search tasks, jobs, connectors…" />
        <CommandList>
          <CommandEmpty>{{ loading ? 'Searching…' : 'No results.' }}</CommandEmpty>
          <CommandGroup heading="Results">
            <CommandItem
              v-for="r in results"
              :key="r.id"
              :value="r.id"
              @select="navigate(r)"
            >
              <Search class="size-4 mr-2 text-muted-foreground" />
              <div class="flex flex-col min-w-0">
                <span class="text-sm truncate">{{ r.name }}</span>
                <span class="text-xs text-muted-foreground truncate">{{ r.type }} · {{ r.description }}</span>
              </div>
            </CommandItem>
          </CommandGroup>
          <CommandGroup v-if="query.trim()" heading="AI">
            <CommandItem :value="`ask-${query}`" @select="askAi({ id: query, type: 'query', name: query, description: '', resource_ref: { type: 'query', id: query }, score: 0 })">
              <Brain class="size-4 mr-2" />
              Ask AI about "{{ query }}"
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </DialogContent>
  </Dialog>
</template>
